import { Mail } from '@mui/icons-material';
import AccessTimeSharpIcon from '@mui/icons-material/AccessTimeSharp';
import { Grid, MenuItem, Select } from '@mui/material';
import { RelationType } from '@thro-types/access-handler';

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';

import { useEntitlementGroupsQuery } from 'api/hooks/accessHandler/entitlementGroup';
import { useSelfEntitlementGroupsQuery } from 'api/hooks/users/self';
import { SettingsTabs } from 'app/components/SettingsTabs';
import LeihBar from 'assets/leihBar_Logo.svg';
import { translations } from 'locales/i18n';
import { SettingsTab } from 'types/AppTypes';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import usePageHeading from 'utils/hooks/usePageHeading';
import useQueryStringState from 'utils/hooks/useQueryStringState';

import { getMyLeihbarEntitlements, isLeihBarAdmin } from '../helper';
import { MailTemplates } from './MailTemplates';
import { OpeningHours } from './OpeningHours';

enum Settings {
  MAILTEMPLATES = 'mailtemplates',
  OPENINGHOURS = 'openingHours'
}

const settingsTabs: SettingsTab[] = [
  { value: Settings.MAILTEMPLATES, icon: <Mail /> },
  { value: Settings.OPENINGHOURS, icon: <AccessTimeSharpIcon /> }
];

export const LenderProfile = () => {
  const { permissions } = useAuthContext();
  const { data: entitlementGroups } = useEntitlementGroupsQuery(isLeihBarAdmin(permissions), {
    params: { relationType: RelationType.LEIHBAR }
  });
  const { t } = useTranslation();
  const [entitlementGroup, setEntitlementGroup] = useState<'' | number>('');

  const [activeTab, setActiveTab] = useQueryStringState<Settings>('tab', Settings.MAILTEMPLATES);

  usePageHeading({ heading: t(translations.pages.leihbar.profile.title()), headingLogo: LeihBar });

  const renderSettingsTab = useCallback(
    (entitlementGroup: number) => {
      switch (activeTab) {
        case Settings.MAILTEMPLATES:
          return <MailTemplates lenderGroupId={entitlementGroup} />;
        case Settings.OPENINGHOURS:
          return <OpeningHours lenderGroupId={entitlementGroup} />;
      }
    },
    [activeTab]
  );

  const { data, isSuccess } = useSelfEntitlementGroupsQuery();

  const myLeihbarEntitlements = getMyLeihbarEntitlements(data);

  useEffect(() => {
    if (isSuccess && myLeihbarEntitlements.length === 1) {
      setEntitlementGroup(myLeihbarEntitlements[0].id);
    }
  }, [isSuccess, myLeihbarEntitlements]);

  if (!myLeihbarEntitlements.length) {
    return <Navigate to={'/leihbar'} />;
  }

  return myLeihbarEntitlements.length > 1 && entitlementGroup === '' ? (
    <Grid
      container
      justifyContent={'center'}
      alignItems={'center'}
      minHeight={'60vh'}
      flexDirection={'column'}
      gap={2}
    >
      <Grid item maxWidth={'20em'} textAlign={'center'}>
        {t(translations.pages.leihbar.profile.selectEntitlementGroup())}
      </Grid>
      <Select
        value={entitlementGroup}
        onChange={e => setEntitlementGroup(parseInt(e.target.value))}
        sx={{ width: '20em' }}
        size="small"
      >
        {isLeihBarAdmin(permissions)
          ? entitlementGroups?.data.map(ent => (
              <MenuItem key={ent.id} value={ent.id}>
                {ent.name}
              </MenuItem>
            ))
          : myLeihbarEntitlements.map(ent => (
              <MenuItem key={ent.id} value={ent.id}>
                {ent.name}
              </MenuItem>
            ))}
      </Select>
    </Grid>
  ) : (
    entitlementGroup !== '' && (
      <Grid container gap={2} justifyContent={'center'} minHeight={'50vh'}>
        <div style={{ position: 'sticky', top: 0 }}>
          <SettingsTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            tabs={settingsTabs}
            i18nDictionary={translations.pages.leihbar.profile.tabs}
          />
        </div>
        <Grid item xs>
          {renderSettingsTab(entitlementGroup)}
        </Grid>
      </Grid>
    )
  );
};
